import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { fmtDate, originBase } from './util';
import TabConfigurar from './tabs/Configurar';
import TabProductos from './tabs/Productos';
import TabPromociones from './tabs/Promociones';
import TabIngreso from './tabs/Ingreso';
import TabCajas from './tabs/Cajas';
import TabMesas from './tabs/Mesas';
import TabVentas from './tabs/Ventas';
import TabAdministracion from './tabs/Administracion';
import TabPersonal from './tabs/Personal';
import TabPagos from './tabs/Pagos';
import GenerarEntradas from './GenerarEntradas';
import { usePlan, habilitada, EtiquetaPlan, PlanBloqueado } from '../components/Plan';

// Pestañas que dependen del plan (ver planes.js en el backend).
const FUNCION_POR_PESTANA = { cajas: 'tpv', mesas: 'mesas', promociones: 'promociones' };

// Orden por lo que más se mira al entrar a un evento: primero lo vendido/plata; Configurar
// va al final (una vez creado el evento casi no se toca). Notas se movió a Administración.
const TABS = [
  ['ventas', 'Entradas vendidas'],
  ['administracion', 'Administración'],
  ['ingreso', 'Ingreso'],
  ['cajas', 'Cajas'],
  ['mesas', 'Mesas'],
  ['personal', 'RRPP'],
  ['productos', 'Lotes'],
  ['promociones', 'Promociones'],
  ['pagos', 'Pagos'],
  ['configurar', 'Configurar'],
];

export default function EventDetail({ eventId, onBack }) {
  const api = useApi();
  const [event, setEvent] = useState(null);
  // La pestaña abierta también va en la URL (?t=…): refrescar mientras mirás la caja o la
  // rendición te deja en la misma pestaña, no en "Entradas vendidas".
  const [params, setParams] = useSearchParams();
  const plan = usePlan();
  const tab = TABS.some(([k]) => k === params.get('t')) ? params.get('t') : 'ventas';
  const setTab = (k) => {
    const next = new URLSearchParams(params);
    if (k === 'ventas') next.delete('t'); else next.set('t', k);
    setParams(next, { replace: true });
  };
  const [genOpen, setGenOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Cada pestaña trae SUS datos una vez, al abrirse. Al generar un link desde el botón de
  // arriba se recargaba el evento, pero la pestaña abierta no se enteraba: la lista de
  // "Entradas vendidas" seguía sin el link nuevo hasta cambiar de pestaña o recargar.
  // Este contador va como `key` del contenedor: al cerrar el formulario cambia, la
  // pestaña activa se vuelve a montar y trae todo de nuevo.
  const [refresco, setRefresco] = useState(0);
  const [errorCarga, setErrorCarga] = useState('');

  const publicUrl = `${originBase()}/e/${eventId}`;
  async function shareEvent() {
    if (navigator.share) {
      try { await navigator.share({ title: event?.name || 'Evento', text: `Entradas: ${event?.name || ''}`, url: publicUrl }); return; }
      catch (e) { /* cancelado */ }
    }
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copiá el link del evento:', publicUrl); }
  }

  const load = useCallback(async () => {
    try {
      const ev = await api.get(`/api/events/${eventId}`);
      setEvent(ev);
      setErrorCarga('');
    } catch (e) {
      // Ahora el id del evento viaja en la URL, así que se puede llegar acá con uno que ya
      // no existe: un evento borrado, o un link viejo. Sin este aviso la pantalla se
      // quedaba en "Cargando evento…" para siempre.
      if (!event) setErrorCarga(e.message || 'No se pudo abrir el evento');
    }
  }, [eventId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (!event) {
    return errorCarga
      ? (
        <div className="lb-card" style={{ padding: '28px 26px', maxWidth: 440 }}>
          <div className="lb-h" style={{ fontSize: 17 }}>No encontramos este evento</div>
          <p style={{ color: 'var(--ink-600)', fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Puede que lo hayas borrado o que el link sea de otro boliche.
          </p>
          <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 14 }} onClick={onBack}>← Volver a eventos</button>
        </div>
      )
      : <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Cargando evento…</div>;
  }

  const tabProps = { event, api, reload: load };

  return (
    <div className="lb-fade">
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <button onClick={onBack} className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginBottom: 12 }}>← Eventos</button>
          <div style={{ fontSize: 11, color: 'var(--ink-600)', textTransform: 'capitalize' }}>{fmtDate(event.date)}</div>
          <div className="lb-display" style={{ fontSize: 30, marginTop: 4 }}>{event.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="lb-btn lb-btn--ghost" onClick={shareEvent}>{copied ? 'Link copiado ✓' : 'Compartir evento ↗'}</button>
          <button className="lb-btn lb-btn--primary" onClick={() => setGenOpen(true)}>Generar entradas →</button>
        </div>
      </div>

      {/* tabs */}
      <div className="lb-subtabs">
        {TABS.map(([id, label]) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}{FUNCION_POR_PESTANA[id] && <EtiquetaPlan plan={plan} funcion={FUNCION_POR_PESTANA[id]} />}</button>
        ))}
      </div>

      <div key={refresco} style={{ paddingTop: 6 }}>
        {FUNCION_POR_PESTANA[tab] && !habilitada(plan, FUNCION_POR_PESTANA[tab])
          ? <PlanBloqueado plan={plan} funcion={FUNCION_POR_PESTANA[tab]} onVerPlanes={() => setParams({ s: 'miplan' })} />
          : (<>
        {tab === 'configurar' && <TabConfigurar {...tabProps} />}
        {tab === 'productos' && <TabProductos {...tabProps} />}
        {tab === 'promociones' && <TabPromociones {...tabProps} />}
        {tab === 'ingreso' && <TabIngreso {...tabProps} />}
        {tab === 'cajas' && <TabCajas {...tabProps} />}
        {tab === 'mesas' && <TabMesas {...tabProps} />}
        {tab === 'ventas' && <TabVentas {...tabProps} />}
        {tab === 'administracion' && <TabAdministracion {...tabProps} />}
        {tab === 'personal' && <TabPersonal {...tabProps} />}
        {tab === 'pagos' && <TabPagos {...tabProps} />}
          </>)}
      </div>

      {genOpen && <GenerarEntradas event={event} api={api} onClose={() => { setGenOpen(false); load(); setRefresco(n => n + 1); }} />}
    </div>
  );
}
